import React, { useEffect, useState, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Form, Button, Col, Row } from "react-bootstrap";
import Select, { MultiValue } from "react-select";
import { AppContext } from "../../AppContext/Context";
import { api } from "../../API";
import Loader from "../loader";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useHandleUnauthorized from "../../hooks/useHandleUnauthorized/UseHandleUnauthorized";
import { config } from "../../config";
import NavigationBackButton from "../navigateback/NavigationBackButton";
import SweetLayout from "../../Layout/SweetLayout";

interface OptionType {
  value: number;
  label: string;
}

interface FormData {
  name: string;
  description: string;
  confidential: boolean;
  owner_account_id: string | number;
  ks: number[];
}

interface KscResponse {
  ksc_id: number;
}

const CreateKsc: React.FC = () => {
  const { getIdTokenClaims, isLoading } = useAuth0();
  const nav = useNavigate();
  const { setIsLoad, isLoad } = useContext(AppContext);
  const [selectedKs, setSelectedKs] = useState<number[]>([]);
  const handleUnauthorized = useHandleUnauthorized();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    confidential: false,
    owner_account_id: "",
    ks: [],
  });

  const clearForm = () => {
    setSelectedKs([]);
    setFormData({
      name: "",
      description: "",
      confidential: false,
      owner_account_id: "",
      ks: [],
    });
  };

  const [ownerOptions, setOwnerOptions] = useState<OptionType[]>([]);
  const [loadingCount, setLoadingCount] = useState<number>(0);
  const [ks, setKss] = useState<OptionType[]>([]);

  useEffect(() => {
    setIsLoad(loadingCount > 0);
  }, [loadingCount, setIsLoad]);

  const handleKsChange = (selectedOptions: MultiValue<OptionType>) => {
    const selectedKsIds = selectedOptions.map((option) => option.value);
    setSelectedKs(selectedKsIds);
    setFormData({
      ...formData,
      ks: selectedKsIds,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!isLoading) {
        setLoadingCount((prevCount) => prevCount + 1);
        const endpoint = `${config.baseURL}/admin/v1/ks`;
        try {
          const idTokenClaims = await getIdTokenClaims();
          const idToken = idTokenClaims?.__raw;
          const response = await api.get(endpoint, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
          setKss(
            response.data.map((ks: any) => ({
              value: ks.ks_id,
              label: `${ks.name} (${ks.ks_id})`,
            }))
          );
        } catch (error) {
          console.error("API request error:", (error as Error).message);
          handleUnauthorized(error);
        } finally {
          setLoadingCount((prevCount) => prevCount - 1);
        }
      }
    };
    fetchData();
  }, [isLoading, getIdTokenClaims, handleUnauthorized]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isLoading) {
        setLoadingCount((prevCount) => prevCount + 1);
        const endpoint = `${config.baseURL}/admin/v1/account`;
        try {
          const idTokenClaims = await getIdTokenClaims();
          const idToken = idTokenClaims?.__raw;
          const response = await api.get(endpoint, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
          setOwnerOptions(
            response.data.map((channel: any) => ({
              value: channel.account_id,
              label: `${channel.name} (${channel.account_id})`,
            }))
          );
        } catch (error) {
          console.error("API request error:", (error as Error).message);
        } finally {
          setLoadingCount((prevCount) => prevCount - 1);
        }
      }
    };
    fetchData();
  }, [isLoading, getIdTokenClaims]);

  const handleChange = (selectedOption: OptionType | null) => {
    setFormData({
      ...formData,
      owner_account_id: selectedOption ? selectedOption.value : "",
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const payload = {
      name: formData.name,
      description: formData.description,
      confidential: formData.confidential,
      owner_account_id: formData.owner_account_id,
      ks_ids: selectedKs,
    };

    setIsLoad(true);
    try {
      const idTokenClaims = await getIdTokenClaims();
      const idToken = idTokenClaims?.__raw;
      const response = await api.post<KscResponse>("/admin/v1/ksc", payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      toast.success("KSC created successfully!");
      setTimeout(() => {
        nav(`/ksc-details/${response?.data?.ksc_id}`);
      }, 3000);
      clearForm();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Error submitting form data:", error);
    } finally {
      setIsLoad(false);
    }
  };

  return (
    <SweetLayout>
    <div>
      {isLoad && (
        <div
          style={{
            position: "absolute",
            left: "60%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            width: "100%",
            top: "60px",
          }}
        >
          <Loader />
        </div>
      )}
      <ToastContainer />
      <NavigationBackButton />
      <div className="">
        <h1>Create Knowledge Source Configuration</h1>
      </div>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6} className="mt-2">
            <Form.Group controlId="formName">
              <Form.Label className="fw-bold">Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                name="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mt-2">
            <Form.Group controlId="formDescription">
              <Form.Label className="fw-bold mt-1">Description</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Description"
                name="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mt-2">
            <Form.Group controlId="formks">
              <Form.Label className="fw-bold mt-1">KSs</Form.Label>
              <Select
                id="ks"
                value={ks.filter((option) => selectedKs.includes(option.value))}
                onChange={handleKsChange}
                options={ks}
                isMulti={true}
                placeholder="Select KS ID"
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mt-2">
            <Form.Group controlId="formOwnerAccountId">
              <Form.Label className="fw-bold mt-1">
                Owner Account ID:
              </Form.Label>
              <Select
                id="channels"
                value={
                  formData.owner_account_id
                    ? ownerOptions.find(
                        (option) => option.value === formData.owner_account_id
                      )
                    : null
                }
                onChange={handleChange}
                options={ownerOptions}
                placeholder="Select Owner Account ID"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6} className="mt-2">
            <Form.Group
              controlId="formConfidential"
              className="d-flex justify-content-start gap-1 align-items-center"
            >
              <Form.Label className="fw-bold mt-2">Confidential</Form.Label>
              <Form.Check
                type="checkbox"
                name="confidential"
                checked={formData.confidential}
                onChange={(e) =>
                  setFormData({ ...formData, confidential: e.target.checked })
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex gap-2 align-items-center mt-3">
          <div>
            <Button variant="primary" type="submit">
              Create KSC
            </Button>
          </div>
          <div>
            <Button variant="danger" onClick={clearForm}>
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    </div>
    </SweetLayout>
  );
};

export default CreateKsc;

